import { Box, Text, TextInput } from "@mantine/core";
import { CaretRightIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Page, PageHeader } from "../../components/layout/Page";
import { ledgerGroupTitleKey } from "../../constants/ledger-sections";
import { useLedgerSections, type VisibleLedgerGroup } from "../../hooks/useLedgerSections";
import classes from "./hub.module.css";

// Left column: Дэвтэр ба бичилт + Харилцагч хоорондын; right: Тайлан + Тохиргоо.
const LEFT = ["book", "intercompany"];
const RIGHT = ["report", "settings"];

export function LedgerHubPage() {
  const { t } = useTranslation("layout");
  const { ledgerOn, groups } = useLedgerSections();
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered: VisibleLedgerGroup[] = groups
    .map((g) => ({
      ...g,
      sections: g.sections.filter(
        (x) => !q || t(x.titleKey).toLowerCase().includes(q) || t(x.descKey).toLowerCase().includes(q),
      ),
    }))
    .filter((g) => g.sections.length > 0);

  const total = groups.reduce((n, g) => n + g.sections.length, 0);
  const col = (keys: string[]) => filtered.filter((g) => keys.includes(g.key));

  const renderGroup = (g: VisibleLedgerGroup) => (
    <section key={g.key} className={classes.card}>
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle}>{t(ledgerGroupTitleKey(g.key))}</h2>
        <span className={classes.count}>{g.sections.length}</span>
      </div>
      {g.sections.map((x) => {
        const inner = (
          <>
            <span className={`${classes.iconBox} ${classes[g.key]}`}>
              <x.icon size={18} weight="duotone" />
            </span>
            <span className={classes.text}>
              <span className={classes.name}>{t(x.titleKey)}</span>
              <span className={classes.desc}>{t(x.descKey)}</span>
            </span>
            {x.supplierOnly ? <span className={classes.tag}>{t("ledgerHub.supplierTag")}</span> : null}
            <CaretRightIcon size={16} className={classes.chev} aria-hidden />
          </>
        );
        return x.disabled ? (
          <div key={x.key} className={`${classes.row} ${classes.rowDisabled}`} aria-disabled="true">
            {inner}
          </div>
        ) : (
          <Link key={x.key} to={x.link} className={classes.row}>
            {inner}
          </Link>
        );
      })}
    </section>
  );

  return (
    <Page>
      <PageHeader
        title={
          <>
            {t("ledgerHub.title")}{" "}
            <span className={`${classes.pill} ${ledgerOn ? classes.pillOn : classes.pillOff}`}>
              <span className={classes.dot} />
              {t(ledgerOn ? "ledgerHub.on" : "ledgerHub.off")}
            </span>
          </>
        }
        subtitle={t("ledgerHub.subtitle", { count: total })}
        extra={
          <TextInput
            className={classes.search}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            placeholder={t("ledgerHub.search")}
            aria-label={t("ledgerHub.searchLabel")}
            leftSection={<MagnifyingGlassIcon size={15} />}
          />
        }
      />
      {!ledgerOn ? <Box className={classes.notice}>{t("ledgerHub.ledgerOff")}</Box> : null}
      {filtered.length === 0 ? (
        <Text className={classes.empty}>{t("ledgerHub.empty")}</Text>
      ) : (
        <div className={classes.grid}>
          <div className={classes.col}>{col(LEFT).map(renderGroup)}</div>
          <div className={classes.col}>{col(RIGHT).map(renderGroup)}</div>
        </div>
      )}
    </Page>
  );
}
